import {
  Alert,
  Card,
  Col,
  Empty,
  InputNumber,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme as antdTheme,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs from 'dayjs';
import { reportsApi, type BackdatedReceiptRow } from '@/api/reports';
import { PageHeader } from '@/shared/components/page-header';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function BackdatedReceiptsReportPage() {
  const { token } = antdTheme.useToken();
  const [minDaysBack, setMinDaysBack] = useState(1);

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'backdated-receipts', minDaysBack],
    queryFn: () => reportsApi.backdatedReceipts(minDaysBack),
  });

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Reports"
        title="Back-dated receipts"
        description="Fee receipts recorded against an older payment date than the day they were actually entered. Honest historical catch-up and back-dating to hide cash look the same in every other report — only the gap between when a receipt was keyed in and its payment date reveals it."
      />

      <Alert
        type="info"
        showIcon
        message="How to read this"
        description={
          <Typography.Text type="secondary">
            <b>Days back</b> = entered date − payment date. A few days is normal (a receipt written on
            Friday, entered Monday). Large gaps, or any row tagged{' '}
            <Tag color="volcano" style={{ marginInline: 2 }}>
              Already handed over
            </Tag>
            — meaning the cash landed in a day the Principal had already taken handover for — are worth a
            second look, because they distort that closed day's cash position.
          </Typography.Text>
        }
      />

      <Space wrap size={12} align="center">
        <Typography.Text type="secondary">Minimum days back</Typography.Text>
        <InputNumber
          min={1}
          max={365}
          value={minDaysBack}
          onChange={(v) => setMinDaysBack(v ?? 1)}
        />
      </Space>

      {data ? (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={8}>
              <Card>
                <Statistic title="Back-dated receipts" value={data.count} />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card>
                <Statistic title="Total amount" value={PKR(data.totalAmount)} prefix="₨" />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card>
                <Statistic
                  title="Landed in a closed (handed-over) day"
                  value={`${data.afterHandoverCount} · ₨ ${PKR(data.afterHandoverAmount)}`}
                  styles={{ content: { color: data.afterHandoverCount > 0 ? token.colorError : undefined } }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Back-dated receipts" styles={{ body: { padding: 0 } }}>
            {data.rows.length === 0 ? (
              <Empty style={{ padding: 24 }} description="No back-dated receipts at this threshold" />
            ) : (
              <Table<BackdatedReceiptRow>
                rowKey="receiptId"
                size="small"
                loading={isFetching}
                pagination={{ pageSize: 50, showSizeChanger: false }}
                dataSource={data.rows}
                columns={[
                  {
                    title: 'Receipt #',
                    dataIndex: 'receiptNumber',
                    width: 100,
                  },
                  {
                    title: 'Student',
                    dataIndex: 'studentName',
                    render: (v: string, r) => (
                      <>
                        <Typography.Text>{v}</Typography.Text>
                        {r.reversed && (
                          <Tag color="default" style={{ marginInlineStart: 6 }}>
                            Reversed
                          </Tag>
                        )}
                      </>
                    ),
                  },
                  {
                    title: 'Payment date',
                    dataIndex: 'paymentDate',
                    width: 120,
                    render: (d: string) => dayjs(d).format('DD MMM YY'),
                  },
                  {
                    title: 'Entered',
                    dataIndex: 'enteredAt',
                    width: 140,
                    render: (d: string) => (
                      <Tooltip title={dayjs(d).format('DD MMM YYYY HH:mm')}>
                        {dayjs(d).format('DD MMM YY')}
                      </Tooltip>
                    ),
                  },
                  {
                    title: 'Days back',
                    dataIndex: 'daysBack',
                    width: 100,
                    align: 'right',
                    sorter: (a, b) => a.daysBack - b.daysBack,
                    render: (n: number) => (
                      <Typography.Text strong style={{ color: n > 7 ? token.colorWarning : undefined }}>
                        {n}
                      </Typography.Text>
                    ),
                  },
                  {
                    title: 'Amount',
                    dataIndex: 'amount',
                    width: 120,
                    align: 'right',
                    sorter: (a, b) => Number(a.amount) - Number(b.amount),
                    render: (v: string) => `₨ ${PKR(v)}`,
                  },
                  {
                    title: 'Entered by',
                    dataIndex: 'enteredBy',
                    width: 130,
                  },
                  {
                    title: '',
                    dataIndex: 'afterHandover',
                    width: 150,
                    render: (v: boolean) =>
                      v ? <Tag color="volcano">Already handed over</Tag> : null,
                  },
                ]}
              />
            )}
          </Card>
        </>
      ) : (
        <Empty />
      )}
    </div>
  );
}
